import { ArrowRight } from 'lucide-react'
import LoopCTAs from './LoopCTAs'
import TrackedCTA from './TrackedCTA'
import FloatingBackground from './FloatingBackground'
import Seo from './Seo'
import { FadeIn } from './Motion'
import { FINAL_CTA_HEADLINE } from '../config/cta'

/**
 * Shared template for /feedback-loops/* pages.
 *
 * Props (all required unless marked optional):
 *   h1                — string
 *   subhead           — string
 *   outerLoopSteps    — string[] e.g. ["Plan", "Execute", "Retro", "Improve"]
 *   gapHeadline       — string
 *   gapBody           — string
 *   stakeholders      — { icon, label, description }[]
 *   stakeholderSectionHeading — optional override, default "Every voice in one loop"
 *   stakeholderSectionSubhead — optional override, default "Run parallel campaigns across every stakeholder. One synthesis at the end."
 *   sampleRetro       — { wentWell, couldHaveBeenBetter, whatsNext } (strings)
 *   audienceCallouts  — { title, body }[]   (optional, used by Validation and Service)
 *   calloutBlock      — { kind: "beta" | "comingSoon", headline, body, ctaText, ctaHref } (optional)
 *   trackingPrefix    — string e.g. "event-loop" (used for TrackedCTA location)
 *   seoTitle          — page title for the browser tab + search results
 *   seoDescription    — meta description (150-160 chars)
 *   seoPath           — route path for canonical URL, e.g. "/feedback-loops/event"
 */
const CALLOUT_BADGE = {
  beta:       { label: 'Beta',        bg: '#8b5cf6' },
  comingSoon: { label: 'Coming soon', bg: '#00008B' },
}

// Logo palette rotated through stakeholders / steps / accents
const LOGO_COLORS = ['#3b82f6', '#8b5cf6', '#00008B']

export default function LoopPage({
  h1,
  subhead,
  outerLoopSteps,
  gapHeadline,
  gapBody,
  stakeholders,
  stakeholderSectionHeading = 'Every voice in one loop',
  stakeholderSectionSubhead = 'Run parallel campaigns across every stakeholder. One synthesis at the end.',
  sampleRetro,
  audienceCallouts,
  calloutBlock,
  trackingPrefix,
  seoTitle,
  seoDescription,
  seoPath,
}) {
  return (
    <div>
      <Seo title={seoTitle} description={seoDescription} path={seoPath} />

      {/* Hero */}
      <section className="relative bg-white py-20 md:py-28 overflow-hidden">
        <FloatingBackground />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <FadeIn>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
                Feedback loop · {trackingPrefix.replace('-loop', '').replace('-', ' ')}
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.98]">
                {h1}
              </h1>
              <p className="text-xl text-intelligence-700 mb-8 leading-relaxed">{subhead}</p>
            </FadeIn>

            {/* Outer loop visual — chunky bordered pills with rotating logo colors */}
            <FadeIn delay={0.1}>
              <div className="flex flex-wrap items-center gap-2 mb-10">
                {outerLoopSteps.map((step, i) => {
                  const color = LOGO_COLORS[i % LOGO_COLORS.length]
                  return (
                    <span key={i} className="flex items-center gap-2">
                      <span
                        className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-intelligence-900 bg-white rounded-full text-sm font-semibold text-intelligence-900 shadow-[2px_2px_0_0_rgba(15,23,42,1)]"
                      >
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                        {step}
                      </span>
                      {i < outerLoopSteps.length - 1 && (
                        <ArrowRight className="w-4 h-4 text-intelligence-400" />
                      )}
                    </span>
                  )
                })}
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <LoopCTAs location={`${trackingPrefix}-hero`} showMicrocopy />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* The Gap */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-2xl bg-[#00008B] translate-x-2 translate-y-2"
              />
              <div className="relative bg-white border-2 border-intelligence-900 rounded-2xl p-8 md:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                  The gap
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                  {gapHeadline}
                </h2>
                <p className="text-lg text-intelligence-700 leading-relaxed">{gapBody}</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Stakeholders */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                Stakeholders
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                {stakeholderSectionHeading}
              </h2>
              <p className="text-lg text-intelligence-600 max-w-2xl mx-auto">
                {stakeholderSectionSubhead}
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stakeholders.map((s, i) => {
              const color = LOGO_COLORS[i % LOGO_COLORS.length]
              return (
                <FadeIn key={s.label} delay={i * 0.05}>
                  <div className="relative group h-full">
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 rounded-xl translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2"
                      style={{ backgroundColor: color }}
                    />
                    <div className="relative bg-white rounded-xl border-2 border-intelligence-900 p-6 h-full">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 border-2 border-intelligence-900"
                        style={{ backgroundColor: `${color}15` }}
                      >
                        <s.icon className="w-6 h-6" style={{ color }} />
                      </div>
                      <h3 className="text-lg font-bold text-intelligence-900 mb-2">{s.label}</h3>
                      <p className="text-intelligence-600">{s.description}</p>
                    </div>
                  </div>
                </FadeIn>
              )
            })}
          </div>
        </div>
      </section>

      {/* Sample Growth Retro */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                Growth retro
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                What you get back
              </h2>
              <p className="text-lg text-intelligence-600">
                Every campaign ends with the same growth retro. Ready to act on.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { label: 'What went well', body: sampleRetro.wentWell, color: '#3b82f6' },
              { label: 'Could have gone better', body: sampleRetro.couldHaveBeenBetter, color: '#8b5cf6' },
              { label: "What's next", body: sampleRetro.whatsNext, color: '#00008B' },
            ].map((col, i) => (
              <FadeIn key={col.label} delay={i * 0.1}>
                <div className="relative h-full">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-xl translate-x-1.5 translate-y-1.5"
                    style={{ backgroundColor: col.color }}
                  />
                  <div className="relative bg-white rounded-xl border-2 border-intelligence-900 p-6 h-full">
                    <div
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4 text-white"
                      style={{ backgroundColor: col.color }}
                    >
                      {col.label}
                    </div>
                    <p className="text-intelligence-700 leading-relaxed">{col.body}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Audience callouts (optional) */}
      {audienceCallouts && audienceCallouts.length > 0 && (
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              {audienceCallouts.map((c, i) => {
                const color = LOGO_COLORS[i % LOGO_COLORS.length]
                return (
                  <FadeIn key={c.title} delay={i * 0.1}>
                    <div className="relative h-full">
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 rounded-xl translate-x-2 translate-y-2"
                        style={{ backgroundColor: color }}
                      />
                      <div className="relative bg-intelligence-100 rounded-xl border-2 border-intelligence-900 p-8 h-full">
                        <p
                          className="font-mono text-xs uppercase tracking-widest mb-3"
                          style={{ color }}
                        >
                          For
                        </p>
                        <h3 className="text-2xl font-bold text-intelligence-900 mb-3">{c.title}</h3>
                        <p className="text-intelligence-700 leading-relaxed">{c.body}</p>
                      </div>
                    </div>
                  </FadeIn>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* Beta / Coming-soon callout (optional) */}
      {calloutBlock && (
        <section className="bg-accent-50 py-16 md:py-20 relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              {(() => {
                const badge = CALLOUT_BADGE[calloutBlock.kind] ?? CALLOUT_BADGE.comingSoon
                return (
                  <div className="relative">
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 rounded-2xl translate-x-2 translate-y-2"
                      style={{ backgroundColor: badge.bg }}
                    />
                    <div className="relative bg-white rounded-2xl border-2 border-intelligence-900 p-10 md:p-12 text-center">
                      <span
                        className="inline-flex items-center gap-2 px-3 py-1 mb-5 border-2 border-intelligence-900 text-white text-xs font-bold uppercase tracking-widest rounded-full shadow-[2px_2px_0_0_rgba(15,23,42,1)]"
                        style={{ backgroundColor: badge.bg }}
                      >
                        {calloutBlock.kind === 'beta' && (
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                        )}
                        {badge.label}
                      </span>
                      <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                        {calloutBlock.headline}
                      </h2>
                      <p className="text-lg text-intelligence-700 mb-8 max-w-2xl mx-auto">
                        {calloutBlock.body}
                      </p>
                      <div className="relative inline-block group">
                        <div
                          aria-hidden="true"
                          className="absolute inset-0 rounded-lg bg-intelligence-900 translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2"
                        />
                        <TrackedCTA
                          href={calloutBlock.ctaHref}
                          location={`${trackingPrefix}-callout`}
                          ctaName={calloutBlock.ctaText}
                          className="relative inline-flex items-center px-8 py-4 text-lg font-bold text-white border-2 border-intelligence-900 rounded-lg"
                          style={{ backgroundColor: badge.bg }}
                        >
                          {calloutBlock.ctaText} <ArrowRight className="ml-2 w-5 h-5" />
                        </TrackedCTA>
                      </div>
                    </div>
                  </div>
                )
              })()}
            </FadeIn>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="bg-intelligence-900 py-20 md:py-24 relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B]" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-[#00008B] via-[#8b5cf6] to-[#3b82f6]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-400 mb-3">
              Your move
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              {FINAL_CTA_HEADLINE}
            </h2>
            <LoopCTAs location={`${trackingPrefix}-final`} align="center" />
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
